import React, { useEffect, useRef } from 'react';
import { ArrowLeft, ArrowRight, Loader2 } from 'lucide-react';
import Modal from './Modal';
import RegistrationStepProgress from './RegistrationStepProgress';

export interface RegistrationStep {
  id: string;
  label: string;
}

interface RegistrationModalShellProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  steps: RegistrationStep[];
  currentStep: number;
  onBack: () => void;
  onNext: () => void;
  onSubmit: () => void;
  isSubmitting?: boolean;
  submitLabel?: string;
  themeClassName?: string;
  presentation?: 'modal' | 'page';
  showPageBackButton?: boolean;
  backLabel?: string;
  continueLabel?: string;
  pageBackLabel?: string;
  children: React.ReactNode;
}

const RegistrationModalShell: React.FC<RegistrationModalShellProps> = ({
  isOpen,
  onClose,
  title,
  subtitle,
  steps,
  currentStep,
  onBack,
  onNext,
  onSubmit,
  isSubmitting = false,
  submitLabel = 'Submit',
  themeClassName,
  presentation = 'modal',
  showPageBackButton = true,
  backLabel = 'Back',
  continueLabel = 'Continue',
  pageBackLabel = 'Registration home',
  children,
}) => {
  const headingRef = useRef<HTMLSpanElement>(null);
  const isLastStep = currentStep === steps.length - 1;

  useEffect(() => {
    if (isOpen) headingRef.current?.focus();
  }, [currentStep, isOpen]);

  const footer = (
    <div className="flex w-full items-center justify-between gap-3">
      <button
        type="button"
        onClick={onBack}
        disabled={currentStep === 0 || isSubmitting}
        className="inline-flex min-h-11 cursor-pointer items-center gap-2 rounded-lg border border-gray-300 px-4 py-2 font-medium text-gray-700 transition-colors duration-200 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-40"
      >
        <ArrowLeft className="h-4 w-4" /> {backLabel}
      </button>
      <button
        type="button"
        onClick={isLastStep ? onSubmit : onNext}
        disabled={isSubmitting}
        className="inline-flex min-h-11 cursor-pointer items-center gap-2 rounded-lg bg-primary px-5 py-2 font-semibold text-on-primary transition-colors duration-200 hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
      >
        {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" /> : null}
        {isLastStep ? submitLabel : continueLabel}
        {!isLastStep && <ArrowRight className="h-4 w-4" />}
      </button>
    </div>
  );

  const formContent = (
    <>
      <RegistrationStepProgress steps={steps.map((step) => step.label)} currentStep={currentStep} />
      {children}
    </>
  );

  if (!isOpen) return null;

  if (presentation === 'page') {
    return (
      <section
        aria-labelledby="registration-form-title"
        className={`mx-auto w-full max-w-4xl overflow-hidden rounded-2xl border border-primary-100 bg-white shadow-[0_24px_70px_-36px_rgb(var(--brand-primary)_/_0.35)] ${themeClassName ?? ''}`}
      >
        <header className="flex items-center justify-between gap-4 border-b border-primary-600 bg-primary px-5 py-5 sm:px-8">
          <div>
            <h1 id="registration-form-title" className="text-xl font-semibold tracking-tight text-white sm:text-2xl">
              <span ref={headingRef} tabIndex={-1} className="block outline-none">{title}</span>
            </h1>
            {subtitle && <p className="mt-1 text-sm text-white/80">{subtitle}</p>}
          </div>
          {showPageBackButton && (
            <button
              type="button"
              onClick={onClose}
              aria-label="Back to registration choices"
              className="inline-flex min-h-11 cursor-pointer items-center justify-center gap-2 rounded-xl px-3 text-sm font-semibold text-white transition-colors duration-200 hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-primary"
            >
              <ArrowLeft className="h-5 w-5" aria-hidden="true" />
              <span className="hidden sm:inline">{pageBackLabel}</span>
            </button>
          )}
        </header>
        <div className="px-5 py-6 sm:px-8 sm:py-8">{formContent}</div>
        <footer className="sticky bottom-0 border-t border-gray-200 bg-white/95 px-5 py-4 backdrop-blur sm:px-8">
          {footer}
        </footer>
      </section>
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      closeOnOverlayClick={false}
      maxWidth="max-w-4xl"
      contentClassName={themeClassName}
      title={
        <span className="block">
          <span ref={headingRef} tabIndex={-1} className="block outline-none">{title}</span>
          {subtitle && <span className="mt-1 block text-sm font-normal text-white/80">{subtitle}</span>}
        </span>
      }
      footer={footer}
    >
      {formContent}
    </Modal>
  );
};

export default RegistrationModalShell;
